import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ImagePlus, Loader2, Search, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  useCatalogSearch,
  useUploadOptionImage,
  type CatalogItem,
  type ModifierKind,
  type ModifierOption,
} from './modifiers.api';

/**
 * One option of a modifier group.
 *
 * Nothing here reaches the API: an option is edited into the group in memory
 * and the whole group is saved at once, which is what makes reordering and
 * adding several options a single write rather than one per row.
 *
 * The three kinds differ only in fields — a side is built from a catalogue item
 * and so has its name and brand filled in and locked, an add-on carries a price
 * and an alcohol flag, a requirement has neither.
 */

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold">{label}</Label>
      {children}
    </div>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-1">
      <div className="min-w-0">
        <Label className="text-sm font-normal">{label}</Label>
        {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

/** The catalogue picker a side option is built from. */
function ItemPicker({ onPick }: { onPick: (item: CatalogItem) => void }) {
  const { t } = useTranslation();
  const search = useCatalogSearch();
  const [query, setQuery] = useState('');

  return (
    <div className="space-y-2">
      <form
        role="search"
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          search.mutate(query.trim());
        }}
      >
        <div className="relative flex-1">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('modifiers.searchItem')}
            aria-label={t('modifiers.searchItem')}
            className="pl-9"
          />
        </div>
        <Button type="submit" disabled={search.isPending}>
          {search.isPending ? (
            <Loader2 className="animate-spin" />
          ) : (
            t('common.search')
          )}
        </Button>
      </form>

      {/* A new search clears the previous answer, so there is nothing to
          keep on screen: stand in for the results while it runs. */}
      {search.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="flex items-center gap-2.5 p-2.5">
              <Skeleton className="size-10 shrink-0 rounded" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-3.5 w-2/3" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            </div>
          ))}
        </div>
      )}

      {search.isSuccess && (search.data.items ?? []).length === 0 && (
        <p className="text-muted-foreground text-xs">
          {t('modifiers.noItemsFound')}
        </p>
      )}

      {(search.data?.items ?? []).length > 0 && (
        <div className="max-h-64 divide-y overflow-y-auto rounded-xl border">
          {search.data!.items.map((item) => (
            <button
              key={item.id}
              type="button"
              className="hover:bg-accent flex w-full items-center gap-2.5 p-2.5 text-left transition-colors"
              onClick={() => {
                onPick(item);
                search.reset();
                setQuery('');
              }}
            >
              {item.itemImage ? (
                <img
                  src={item.itemImage}
                  alt=""
                  className="bg-muted size-10 shrink-0 rounded object-contain"
                />
              ) : (
                <span className="bg-muted size-10 shrink-0 rounded" />
              )}
              <div className="min-w-0">
                <p className="truncate text-sm">{item.name}</p>
                <p className="text-muted-foreground truncate text-xs">
                  {item.brand}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function OptionEditor({
  kind,
  option,
  open,
  onOpenChange,
  onSave,
}: {
  kind: ModifierKind;
  option: ModifierOption | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (option: ModifierOption) => void;
}) {
  const { t } = useTranslation();
  const upload = useUploadOptionImage();
  const fileInput = useRef<HTMLInputElement>(null);

  const [draft, setDraft] = useState<ModifierOption | null>(null);
  const [hydrated, setHydrated] = useState<string | null>(null);

  // Hydrate once per option; the editor stays mounted between openings.
  if (option && hydrated !== option.id) {
    setHydrated(option.id);
    setDraft(option);
  }

  if (!draft || !option) return null;

  const set = (patch: Partial<ModifierOption>) =>
    setDraft((current) => (current ? { ...current, ...patch } : current));

  const isNew = !option.name && !option.imageUrl;
  // A side's name and brand come from the item it was built from.
  const fromCatalogue = kind === 'side';

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-h-[90vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isNew ? t('modifiers.newOption') : t('modifiers.editOption')}
          </AlertDialogTitle>
        </AlertDialogHeader>

        <div className="space-y-3">
          {fromCatalogue && (
            <Field label={t('modifiers.searchItem')}>
              <ItemPicker
                onPick={(item) =>
                  set({
                    brand: item.brand,
                    name: item.name,
                    description: item.description,
                    imageUrl: item.itemImage,
                    itemId: item.id,
                  })
                }
              />
            </Field>
          )}

          {fromCatalogue && (
            <Field label={t('modifiers.brand')}>
              <Input disabled value={draft.brand ?? ''} />
            </Field>
          )}

          <Field label={t('modifiers.name')}>
            <Input
              // Locked for a side: changing it here would make the option
              // disagree with the catalogue item it points at.
              disabled={fromCatalogue}
              value={draft.name ?? ''}
              onChange={(event) => set({ name: event.target.value })}
            />
          </Field>

          <Field label={t('modifiers.description')}>
            <Textarea
              rows={2}
              disabled={fromCatalogue}
              value={draft.description ?? ''}
              onChange={(event) => set({ description: event.target.value })}
            />
          </Field>

          {kind === 'addon' && (
            <Field label={t('modifiers.price')}>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={draft.price ?? 0}
                onChange={(event) => set({ price: Number(event.target.value) })}
              />
            </Field>
          )}

          {kind !== 'side' && (
            <>
              <Toggle
                label={t('modifiers.available')}
                hint={t('modifiers.availableHint')}
                checked={draft.isAvailable}
                onChange={(isAvailable) => set({ isAvailable })}
              />
              <Toggle
                label={t('modifiers.allowVariableAmount')}
                hint={t('modifiers.allowVariableAmountHint')}
                checked={draft.allowVariableAmount}
                onChange={(allowVariableAmount) => set({ allowVariableAmount })}
              />
            </>
          )}

          {kind === 'addon' && (
            <Toggle
              label={t('modifiers.alcoholicBeverage')}
              checked={!!draft.isAlcoholicBeverage}
              onChange={(isAlcoholicBeverage) => set({ isAlcoholicBeverage })}
            />
          )}

          <Field label={t('modifiers.image')}>
            <div className="space-y-2">
              {draft.imageUrl && (
                <img
                  src={draft.imageUrl}
                  alt=""
                  className="bg-muted mx-auto max-h-32 rounded-lg object-contain"
                />
              )}
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  upload.mutate(file, {
                    onSuccess: (imageUrl) => set({ imageUrl }),
                    onError: () => toast.error(t('modifiers.uploadFailed')),
                  });
                  event.target.value = '';
                }}
              />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 border-dashed"
                  disabled={upload.isPending}
                  onClick={() => fileInput.current?.click()}
                >
                  {upload.isPending ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <ImagePlus />
                  )}
                  {t('modifiers.uploadImage')}
                </Button>
                {draft.imageUrl && (
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label={t('modifiers.clearImage')}
                    onClick={() => set({ imageUrl: '' })}
                  >
                    <X />
                  </Button>
                )}
              </div>
            </div>
          </Field>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!draft.name?.trim()}
            onClick={() => {
              onSave(draft);
              onOpenChange(false);
            }}
          >
            {isNew ? t('modifiers.add') : t('common.save')}
          </Button>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
